import { cloneElement, type ComponentChildren, type Ref, type TargetedEvent } from 'preact';

import type { DefaultTemplateRootProps, TemplateResult } from 'Components/types';
import productConfig from 'Models/uiConfig/productConfig';
import type { ProductImageCrop } from 'Models/uiConfig/productConfig.types';
import uiConfig from 'Models/uiConfig/uiConfig';
import { uniqueStrings } from 'Utils/array';
import isTruthy from 'Utils/isTruthy';
import type { ForwardedProps, MaybeHotModule } from 'Utils/rtHmrTemplate';

export type CloneSafeExtras = {
  appendedClasses?: string | (string | false | undefined)[];
  appendedChildren?: ComponentChildren | ComponentChildren[] | null;
};

type CloneSafeForwardedProps<RootProps extends object> = Partial<Omit<RootProps, 'children' | 'className'>>;

export type CloneSafeProps<RootProps extends object = DefaultTemplateRootProps<HTMLElement>> =
  CloneSafeForwardedProps<RootProps> & CloneSafeExtras;

function hasHotModuleApi(moduleRef: NodeModule): moduleRef is NodeModule & { hot?: MaybeHotModule['hot'] } {
  return 'hot' in moduleRef;
}

function getClassList(className: unknown): string[] {
  return typeof className === 'string' ? className.split(' ') : [];
}

function getAppendedClassList(appendedClasses: CloneSafeExtras['appendedClasses']): string[] {
  return typeof appendedClasses === 'string'
    ? appendedClasses.split(' ')
    : (appendedClasses ?? []).filter(isTruthy);
}

function flattenChildren(
  ...values: Array<ComponentChildren | ComponentChildren[] | null | undefined>
): ComponentChildren[] {
  return values.flatMap((value) => (Array.isArray(value) ? value : [value])).filter(isTruthy);
}

// ref argument was added to remind to add ref or intentionally omit it
export function cloneSafe<
  RootElement extends HTMLElement = HTMLElement,
  RootProps extends DefaultTemplateRootProps<RootElement> = DefaultTemplateRootProps<RootElement>,
>(
  elem: TemplateResult<RootProps, RootElement>,
  ref: Ref<RootElement> | null,
  options: CloneSafeProps<RootProps> = {},
): TemplateResult<RootProps, RootElement> {
  if (elem === null) {
    return null;
  }

  const { appendedClasses, appendedChildren = null, ...rawProps } = options;
  const props = rawProps as unknown as CloneSafeForwardedProps<RootProps>;

  if (hasHotModuleApi(module) && module.hot && elem.props.hmrable) {
    const delayedCloneProps: CloneSafeProps<RootProps> = { appendedClasses, appendedChildren, ...props };
    const forwardedProps: ForwardedProps<RootProps, RootElement> = {
      delayedCloneSafe: (innerElem, innerRef) => cloneSafe(innerElem, innerRef, delayedCloneProps),
      forwardedRef: ref,
    };
    return cloneElement(elem, forwardedProps);
  }

  const elemClassList = getClassList(elem.props.className);
  const classList = elemClassList.concat(getAppendedClassList(appendedClasses));
  const className = uniqueStrings(classList.filter(isTruthy)).join(' ');

  const children = flattenChildren(elem.props.children, appendedChildren);

  return cloneElement(elem, { ...props, className, ref }, ...children);
}

export function createOnImageError(): (
  e: TargetedEvent<HTMLImageElement, Event>,
  key: string | number | null,
) => void {
  const handledErrors: Record<string, boolean> = {};
  return (e, key = null) => {
    if (!handledErrors[`${key}`]) {
      e.currentTarget.src = productConfig.noImageSrc;
      handledErrors[`${key}`] = true;
    }
  };
}

// https://www.shopify.com/partners/blog/img-url-filter Crop section
export function setImageSize(
  image: string | null,
  width?: number,
  height?: number,
  crop?: ProductImageCrop,
): string | null {
  if (uiConfig.platform === 'shopify' && image) {
    const url = new URL(image);

    if (width) {
      url.searchParams.set('width', width.toString());
    }
    if (height) {
      url.searchParams.set('height', height.toString());
    }
    if (crop) {
      url.searchParams.set('crop', crop);
    }

    return url.href;
  }

  return image;
}

export function preventFormSubmission(e: TargetedEvent<HTMLFormElement, Event>): void {
  e.preventDefault();
  e.stopPropagation();
}
